import { useState, type FormEvent } from "react";
import { Loader2, MessageSquare, Send } from "lucide-react";
import { submitBlogComment } from "@/lib/blogApi";
import { LazyRecaptchaProvider } from "@/components/security/LazyRecaptchaProvider";
import { useRecaptchaToken } from "@/hooks/useRecaptchaToken";
import { cn } from "@/lib/utils";
import type { BlogComment } from "@/types/blog";

type BlogCommentsProps = {
  blogId: number;
  comments: BlogComment[];
  locale: "tr" | "en";
};

function CommentItem({
  comment,
  locale,
  onReply,
}: {
  comment: BlogComment;
  locale: "tr" | "en";
  onReply: (parentId: number) => void;
}) {
  const isTr = locale === "tr";

  return (
    <li className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4 sm:p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-semibold text-[var(--text)]">{comment.name}</p>
        <time className="text-xs text-[var(--text-sm)]">{comment.date}</time>
      </div>
      <p className="mt-2 text-sm leading-relaxed text-[var(--text-md)]">{comment.body}</p>
      <button
        type="button"
        onClick={() => onReply(comment.id)}
        className="mt-3 text-xs font-semibold text-accent-link transition hover:opacity-80"
      >
        {isTr ? "Yanıtla" : "Reply"}
      </button>

      {comment.replies.length > 0 ? (
        <ul className="mt-4 space-y-3 border-l border-[var(--border)] pl-4">
          {comment.replies.map((reply) => (
            <CommentItem key={reply.id} comment={reply} locale={locale} onReply={onReply} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

type BlogCommentsFormProps = BlogCommentsProps & {
  onActivateRecaptcha: () => void;
};

function BlogCommentsForm({ blogId, comments, locale, onActivateRecaptcha }: BlogCommentsFormProps) {
  const { getToken } = useRecaptchaToken();
  const isTr = locale === "tr";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [body, setBody] = useState("");
  const [website, setWebsite] = useState("");
  const [parentId, setParentId] = useState<number | undefined>();
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setStatus("loading");
    setErrorMessage("");

    const recaptchaToken = await getToken("blog_comment");
    const result = await submitBlogComment({
      blogId,
      parentId,
      name,
      email,
      body,
      website,
      recaptchaToken,
    });

    if (result.ok) {
      setStatus("success");
      setName("");
      setEmail("");
      setBody("");
      setWebsite("");
      setParentId(undefined);
      return;
    }

    setStatus("error");
    setErrorMessage(
      isTr
        ? "Yorum gönderilemedi. Lütfen bilgileri kontrol edip tekrar deneyin."
        : "Could not submit your comment. Please check the form and try again."
    );
  }

  return (
    <section className="border-t border-[var(--border)] pt-10">
      <div className="flex items-center gap-2">
        <MessageSquare className="size-5 text-accent-link" aria-hidden />
        <h2 className="text-lg font-semibold tracking-tight text-[var(--text)]">
          {isTr ? "Yorumlar" : "Comments"}
          {comments.length > 0 ? (
            <span className="ml-2 text-sm font-medium text-[var(--text-sm)]">({comments.length})</span>
          ) : null}
        </h2>
      </div>

      {comments.length > 0 ? (
        <ul className="mt-6 space-y-4">
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              locale={locale}
              onReply={(id) => {
                setParentId(id);
                document.getElementById("blog-comment-form")?.scrollIntoView({ behavior: "smooth" });
              }}
            />
          ))}
        </ul>
      ) : (
        <p className="mt-4 text-sm text-[var(--text-md)]">
          {isTr ? "Henüz yorum yok. İlk yorumu siz yazın." : "No comments yet. Be the first to comment."}
        </p>
      )}

      <form id="blog-comment-form" onSubmit={handleSubmit} className="mt-8 space-y-4">
        {parentId ? (
          <p className="text-sm text-[var(--text-md)]">
            {isTr ? "Bir yoruma yanıt yazıyorsunuz." : "You are replying to a comment."}{" "}
            <button
              type="button"
              className="font-semibold text-accent-link"
              onClick={() => setParentId(undefined)}
            >
              {isTr ? "İptal" : "Cancel"}
            </button>
          </p>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[var(--text-sm)]">
              {isTr ? "Ad Soyad" : "Name"}
            </span>
            <input
              type="text"
              required
              maxLength={120}
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="w-full rounded-xl border border-[var(--border)] bg-[var(--card)] px-3.5 py-2.5 text-sm text-[var(--text)] outline-none transition focus:border-enabase-blue/40"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[var(--text-sm)]">
              {isTr ? "E-posta" : "Email"}
            </span>
            <input
              type="email"
              required
              maxLength={160}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="w-full rounded-xl border border-[var(--border)] bg-[var(--card)] px-3.5 py-2.5 text-sm text-[var(--text)] outline-none transition focus:border-enabase-blue/40"
            />
          </label>
        </div>

        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[var(--text-sm)]">
            {isTr ? "Yorum" : "Comment"}
          </span>
          <textarea
            required
            maxLength={4000}
            rows={5}
            value={body}
            onFocus={onActivateRecaptcha}
            onChange={(event) => {
              onActivateRecaptcha();
              setBody(event.target.value);
            }}
            className="w-full resize-y rounded-xl border border-[var(--border)] bg-[var(--card)] px-3.5 py-2.5 text-sm text-[var(--text)] outline-none transition focus:border-enabase-blue/40"
          />
        </label>

        <input
          type="text"
          name="website"
          value={website}
          onChange={(event) => setWebsite(event.target.value)}
          tabIndex={-1}
          autoComplete="off"
          className="hidden"
          aria-hidden
        />

        {status === "success" ? (
          <p className="rounded-xl border border-emerald-500/25 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-700 dark:text-emerald-300">
            {isTr
              ? "Yorumunuz alındı. Onaylandıktan sonra yayınlanacaktır."
              : "Your comment was received and will appear after moderation."}
          </p>
        ) : null}

        {status === "error" ? (
          <p className="rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-300">
            {errorMessage}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={status === "loading"}
          className={cn(
            "inline-flex items-center gap-2 rounded-xl bg-enabase-blue px-5 py-2.5 text-sm font-semibold text-white transition",
            "hover:bg-enabase-blue/90 disabled:cursor-not-allowed disabled:opacity-70"
          )}
        >
          {status === "loading" ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
          {isTr ? "Yorum Gönder" : "Submit comment"}
        </button>
      </form>
    </section>
  );
}

export function BlogComments(props: BlogCommentsProps) {
  const [recaptchaActive, setRecaptchaActive] = useState(false);

  return (
    <LazyRecaptchaProvider active={recaptchaActive}>
      <BlogCommentsForm {...props} onActivateRecaptcha={() => setRecaptchaActive(true)} />
    </LazyRecaptchaProvider>
  );
}
